
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import CustomerNavbar from '../components/CustomerNavbar';
import CaregiverNavbar from '../components/CaregiverNavbar';
import MobileFooter from '../components/MobileFooter';
import axios from 'axios';
import { getFileUrl } from '../config/api';
import { useNotification } from '../context/NotificationContext';
import LocationPicker from '../components/LocationPicker';
import ServiceAreaPicker, { ServiceArea } from '../components/ServiceAreaPicker';

// --- HospitalSearchBox component ---
interface HospitalSearchBoxProps {
  value: { name?: string; address?: string; phone?: string };
  onSelect: (hospital: { name: string; address: string; phone: string }) => void;
}

const HospitalSearchBox: React.FC<HospitalSearchBoxProps> = ({ value, onSelect }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(value.name || '');
  const [address, setAddress] = useState(value.address || '');
  const [phone, setPhone] = useState(value.phone || '');

  useEffect(() => {
    setInputValue(value.name || '');
    setAddress(value.address || '');
    setPhone(value.phone || '');
  }, [value]);

  useEffect(() => {
    if (!window.google || !inputRef.current) return;
    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['establishment'],
      componentRestrictions: { country: [] },
    });
    autocomplete.setFields(['name', 'formatted_address', 'formatted_phone_number']);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.name && place.formatted_address) {
        const fetchedPhone = place.formatted_phone_number || '';
        setInputValue(place.name);
        setAddress(place.formatted_address);
        setPhone(fetchedPhone);
        onSelect({ 
          name: place.name, 
          address: place.formatted_address,
          phone: fetchedPhone
        });
      }
    });
    // Clean up
    return () => {
      window.google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [onSelect]);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        className="w-full px-3 py-2 border rounded-lg"
        placeholder="Search for hospital..."
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        autoComplete="off"
      />
      {address && (
        <div className="mt-1 space-y-0.5">
          <p className="text-gray-500 text-xs">{address}</p>
          {phone && <p className="text-gray-500 text-xs">{phone}</p>}
        </div>
      )}
    </div>
  );
};

const libraries: ("places")[] = ["places"];

interface ProfileData {
  _id: string;
  email: string;
  userType: 'caregiver' | 'customer';
  firstName: string;
  lastName: string;
  phone: string;
  profilePhoto?: string;
  nationalId?: {
    frontSide?: string;
    backSide?: string;
    number?: string;
    type?: string;
    verified?: boolean;
    verifiedAt?: string;
  };
  isVerified?: boolean;
  acceptsDigitalPayment?: boolean;
  // Caregiver fields
  bio?: string;
  yearsOfExperience?: number;
  experiences?: {
    years: number;
    role: string;
    place: string;
  }[];
  servicesOffered?: string[];
  areaOfService?: {
    city: string;
    address: string;
    zipCode?: string;
    coordinates?: [number, number];
    state?: string;
  };
  serviceAreas?: {
    name: string;
    address: string;
    location: {
      type: string;
      coordinates: number[][][]; // Polygon coordinates
    };
  }[];
  // Customer fields
  serviceLocation?: {
    address: string;
    city: string;
    zipCode?: string;
    coordinates?: [number, number];
    state?: string;
  };
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  medicalProfile?: {
    chronicConditions?: string[];
    otherConditions?: string;
    currentMedications?: Array<{
        name: string;
        dosage: string;
        frequency: string;
        prescribedBy?: string;
    }>;
    medicationNotes?: string;
    bloodGroup?: string;
    allergies?: string;
    dietaryRestrictions?: string;
    mobilityLevel?: string;
    specialNeeds?: string;
  };
  rating?: {
    average: number;
    count: number;
  };

  // Added for customer hospital details
  preferredHospital?: {
    name?: string;
    address?: string;
    phone?: string;
  };
}

interface Review {
  _id: string;
  rating: number;
  reviewText?: string;
  customer?: { firstName: string; lastName: string; profilePhoto: string };
  caregiver?: { firstName: string; lastName: string; profilePhoto: string };
  createdAt: string;
}

const Profile: React.FC = () => {
  usePageTitle('My Profile');
  const { user, logout } = useAuth();
  const { pushEnabled, subscribeToPush } = useNotification();
  
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries
  });

  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Edit Mode
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<ProfileData>>({});
  
  // Experience State
  const [currentExp, setCurrentExp] = useState({ years: 0, role: '', place: '' });

  const addExperience = () => {
    if (currentExp.years > 0 && currentExp.role && currentExp.place) {
      setFormData(prev => ({
        ...prev,
        experiences: [...(prev.experiences || []), currentExp]
      }));
      setCurrentExp({ years: 0, role: '', place: '' });
    }
  };

  const removeExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experiences: (prev.experiences || []).filter((_, i) => i !== index)
    }));
  };

  const initialServiceAreas = useMemo(() => {
    // Use formData.serviceAreas if available (during editing), otherwise fallback to profileData
    const sourceAreas = formData.serviceAreas || profileData?.serviceAreas || []; 
    return sourceAreas.map((a: any) => {
        let bounds: google.maps.LatLngBoundsLiteral | undefined;
        let lat = 0, lng = 0;
        
        if (a.location?.type === 'Polygon' && a.location.coordinates[0]) {
            const coords = a.location.coordinates[0];
            const lats = coords.map((c: number[]) => c[1]);
            const lngs = coords.map((c: number[]) => c[0]);
            const north = Math.max(...lats);
            const south = Math.min(...lats);
            const east = Math.max(...lngs);
            const west = Math.min(...lngs);
            
            bounds = { north, south, east, west };
            lat = (north + south) / 2;
            lng = (east + west) / 2;
        } else if (a.location?.coordinates) {
                lng = a.location.coordinates[0];
                lat = a.location.coordinates[1];
        }

        return {
            id: a._id || a.name + (a.lat || lat), // Note: Using properties that might not exist on backend format might be an issue, but 'name' and 'address' should exist
            name: a.name,
            address: a.address,
            lat,
            lng,
            bounds
        };
    });
  }, [formData.serviceAreas, profileData?.serviceAreas]);

  const handleServiceAreasChange = useCallback((areas: any[]) => {
    const backendAreas = areas.map(a => {
        let polygonCoords: number[][];
        if (a.bounds) {
            const { north, south, east, west } = a.bounds;
            polygonCoords = [
                [west, south], [east, south], [east, north], [west, north], [west, south]
            ];
        } else {
            const d = 0.045;
            polygonCoords = [
                [a.lng - d, a.lat - d], [a.lng + d, a.lat - d],
                [a.lng + d, a.lat + d], [a.lng - d, a.lat + d],
                [a.lng - d, a.lat - d]
            ];
        }

        return {
            name: a.name,
            address: a.address,
            location: {
                type: 'Polygon',
                coordinates: [polygonCoords]
            }
        };
    });
    setFormData(prev => ({ ...prev, serviceAreas: backendAreas }));
  }, []);

  // Reviews
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Location Picker Modal
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const prevServiceAreas = useRef<any[]>([]); // Snapshot for reverting changes on Cancel

  useEffect(() => {
    fetchProfile();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  
  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch user profile
      const response = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data.user);
      setFormData(response.data.user);
      
      // Fetch reviews
      if (response.data.user?._id && response.data.user?.userType) {
        fetchReviews(response.data.user._id, response.data.user.userType);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (userId: string, userType: string) => {
    try {
      setReviewsLoading(true);
      const endpoint = userType === 'caregiver' 
        ? `/api/reviews/caregiver/${userId}` 
        : `/api/reviews/customer/${userId}`;
        
      const response = await axios.get(endpoint);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNestedChange = (parent: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev as any)[parent],
        [field]: value
      }
    }));
  };
  
  const handleServiceChange = (service: string) => {
    const currentServices = formData.servicesOffered || [];
    const newServices = currentServices.includes(service)
      ? currentServices.filter(s => s !== service)
      : [...currentServices, service];
      
    setFormData(prev => ({ ...prev, servicesOffered: newServices }));
  };

  const handleConditionToggle = (condition: string) => {
    setFormData(prev => {
        const currentConditions = prev.medicalProfile?.chronicConditions || [];
        const newConditions = currentConditions.includes(condition)
            ? currentConditions.filter(c => c !== condition)
            : [...currentConditions, condition];
            
        return {
            ...prev,
            medicalProfile: {
                ...prev.medicalProfile,
                chronicConditions: newConditions
            }
        };
    });
  };

  const handleLocationSelect = (location: any) => {
    const locationData = {
      address: location.address,
      city: location.city,
      zipCode: location.zipCode,
      state: location.state,
      coordinates: [location.lng, location.lat]
    };

    if (user?.userType === 'caregiver') {
      setFormData(prev => ({ ...prev, areaOfService: locationData as any }));
    } else {
       setFormData(prev => ({ ...prev, serviceLocation: locationData as any }));
    }
    setShowLocationPicker(false);
  };

  /*
  const handleTestNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/notifications/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Test notification sent! Check your device notifications.');
    } catch (err) {
      console.error(err);
      alert('Failed to send test notification. Make sure notifications are enabled for this site.');
    }
  };
  */

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const endpoint = user.userType === 'customer' 
        ? `/api/customers/${user.id}`
        : `/api/caregivers/${user.id}`;
      
      const dataToSubmit = { ...formData };
      
      // Recalculate total years if updating experiences for caregiver
      if (user.userType === 'caregiver' && dataToSubmit.experiences) {
         const totalYears = dataToSubmit.experiences.reduce((acc, curr) => acc + curr.years, 0);
         dataToSubmit.yearsOfExperience = totalYears;
      }

      await axios.put(endpoint, dataToSubmit, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Profile updated successfully!');
      setIsEditing(false);
      fetchProfile(); // Refresh
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">

      <div className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full pb-20 sm:pb-8 animate-fade-in">
        
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            
            {/* Left Column: Photo & Basic Info */}
            <div className="w-full md:w-1/3 flex flex-col items-center">
              <div className="relative mb-4">
                {profileData?.profilePhoto ? (
                  <img 
                    src={getFileUrl(profileData.profilePhoto)}
                    alt="Profile" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary-100"
                  />
                ) : (
                  <div className="w-32 h-32 bg-primary-100 rounded-full flex items-center justify-center text-4xl font-bold text-primary-600">
                    {profileData?.firstName?.charAt(0)}
                  </div>
                )}
                {/* Photo Edit Overlay (Visual only for now) */}
                {isEditing && (
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center cursor-not-allowed" title="Photo update coming soon">
                     <svg className="w-8 h-8 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                )}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 text-center mb-1">
                {profileData?.firstName} {profileData?.lastName}
              </h2>
              <p className="text-gray-500 capitalize mb-4">{profileData?.userType}</p>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mb-4 bg-yellow-50 px-4 py-2 rounded-full border border-yellow-100">
                <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="font-bold text-gray-900">{profileData?.rating?.average.toFixed(1) || 'New'}</span>
                <span className="text-gray-500 text-sm">({profileData?.rating?.count || 0} reviews)</span>
              </div>

               {/* Verification Badge */}
               {user?.userType === 'caregiver' && (
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                  profileData?.nationalId?.verified ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {profileData?.nationalId?.verified ? (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      Verified Caregiver
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Verification Pending
                    </>
                  )}
                </div>
               )}
            </div>

            {/* Right Column: Details Form */}
            <div className="w-full md:w-2/3 space-y-6">
              
              {/* Personal Info Section */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">First Name</label>
                    {isEditing ? (
                      <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
                    ) : (
                      <p className="font-medium text-gray-900">{profileData?.firstName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Name</label>
                    {isEditing ? (
                       <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
                    ) : (
                      <p className="font-medium text-gray-900">{profileData?.lastName}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                    <p className="font-medium text-gray-500 bg-gray-100 px-3 py-2 rounded-lg truncate cursor-not-allowed border border-transparent">
                      {profileData?.email}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                     {isEditing ? (
                       <input type="text" name="phone" value={formData.phone || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" />
                    ) : (
                      <p className="font-medium text-gray-900">{profileData?.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Caregiver Specifics */}
              {user?.userType === 'caregiver' && (
                <>
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                       <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      Professional Info
                    </h3>
                    
                     <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Bio</label>
                          {isEditing ? (
                            <textarea name="bio" value={formData.bio || ''} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Tell us about yourself..." />
                          ) : (
                            <p className="font-medium text-gray-900 whitespace-pre-wrap">{profileData?.bio || 'No bio provided'}</p>
                          )}
                        </div>

                        <div className="space-y-1">
                           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Professional Experience</label>
                           
                           {/* Experience List */}
                           <div className="space-y-3 mb-4">
                             {(isEditing ? formData.experiences : profileData?.experiences)?.map((exp, index) => (
                               <div key={index} className="flex flex-wrap items-center justify-between p-3 bg-white border rounded-lg shadow-sm gap-2">
                                 <div className="min-w-0 flex-1">
                                   <p className="font-bold text-gray-900 break-words">{exp.role}</p>
                                   <p className="text-sm text-gray-500 break-words">{exp.years} years • {exp.place}</p>
                                 </div>
                                 {isEditing && (
                                   <button
                                     onClick={() => removeExperience(index)}
                                     className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                                   >
                                     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                                   </button>
                                 )}
                               </div>
                             ))}
                             {(!profileData?.experiences?.length && !isEditing && !formData.experiences?.length) && (
                               <p className="text-gray-500 italic">No experience records added.</p>
                             )}
                           </div>

                           {/* Add Experience Form */}
                           {isEditing && (
                             <div className="bg-white p-4 rounded-xl border border-dashed border-gray-300">
                               <h4 className="text-sm font-semibold text-gray-700 mb-3">Add New Record</h4>
                               <div className="grid grid-cols-1 gap-3">
                                 <div>
                                   <input
                                     type="text"
                                     placeholder="Role (e.g. Senior Nurse)"
                                     value={currentExp.role}
                                     onChange={(e) => setCurrentExp({...currentExp, role: e.target.value})}
                                     className="w-full px-3 py-2 border rounded-lg text-sm"
                                   />
                                 </div>
                                 <div className="flex flex-wrap gap-2">
                                   <input
                                     type="text"
                                     placeholder="Institution"
                                     value={currentExp.place}
                                     onChange={(e) => setCurrentExp({...currentExp, place: e.target.value})}
                                     className="flex-1 min-w-[140px] px-3 py-2 border rounded-lg text-sm"
                                   />
                                   <input
                                     type="number"
                                     placeholder="Yrs"
                                     min="0"
                                     value={currentExp.years || ''}
                                     onChange={(e) => setCurrentExp({...currentExp, years: parseInt(e.target.value) || 0})}
                                     className="w-20 px-3 py-2 border rounded-lg text-sm"
                                   />
                                   <button
                                     type="button"
                                     onClick={addExperience}
                                     disabled={!currentExp.role || !currentExp.place || currentExp.years <= 0}
                                     className="px-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                   >
                                     Add
                                   </button>
                                 </div>
                               </div>
                             </div>
                           )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-1 md:col-span-2">
                             <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Service Areas</label>
                             {isEditing ? (
                               <div className="space-y-2">
                                  {/* List of currently selected areas */}
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {(formData.serviceAreas || profileData?.serviceAreas || []).map((area: any, idx: number) => (
                                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {area.name}
                                        </span>
                                    ))}
                                    {(!formData.serviceAreas && !profileData?.serviceAreas) && <span className="text-sm text-gray-400">No areas selected</span>}
                                  </div>

                                  <button 
                                    type="button"
                                    onClick={() => {
                                        // Snapshot for cancel functionality
                                        prevServiceAreas.current = JSON.parse(JSON.stringify(formData.serviceAreas || []));
                                        setShowLocationPicker(true);
                                    }}
                                    className="w-full py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center justify-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    Manage Service Areas
                                  </button>
                               </div>
                             ) : (
                               <div className="space-y-1">
                                    {(profileData?.serviceAreas && profileData.serviceAreas.length > 0) ? (
                                        <div className="flex flex-wrap gap-2">
                                            {profileData.serviceAreas.map((area: any, idx: number) => (
                                                <span key={idx} title={area.address} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                                                    {area.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="font-medium text-gray-900">
                                            {profileData?.areaOfService?.city ? `${profileData.areaOfService.address}, ${profileData.areaOfService.city}` : 'No service area set'}
                                        </p>
                                    )}
                               </div>
                             )}
                          </div>
                        </div>

                         <div className="space-y-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Services Offered</label>
                            <div className="flex flex-wrap gap-2">
                              {['nurse-care', 'everyday-assistance'].map(service => (
                                <button
                                  key={service}
                                  type="button"
                                  disabled={!isEditing}
                                  onClick={() => handleServiceChange(service)}
                                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    (formData.servicesOffered || []).includes(service)
                                      ? 'bg-primary-600 text-white'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {service === 'nurse-care' ? 'Nurse Care' : 'Everyday Assistance'}
                                </button>
                              ))}
                            </div>
                         </div>
                     </div>
                  </div>
                </>
              )}

              {/* Customer Specifics */}
               {user?.userType === 'customer' && (
                 <>
                   <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                     <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                       <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                       Service Location
                     </h3>
                     <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-1">
                             <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address</label>
                             {isEditing ? (
                               <div className="space-y-2">
                                  <input readOnly type="text" value={formData.serviceLocation?.address || ''} className="w-full px-3 py-2 border rounded-lg bg-gray-50" />
                                  <button 
                                    type="button"
                                    onClick={() => setShowLocationPicker(true)}
                                    className="w-full py-2 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center justify-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                    Update Location from Map
                                  </button>
                               </div>
                             ) : (
                               <p className="font-medium text-gray-900">{profileData?.serviceLocation?.address}</p>
                             )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <div className="space-y-1">
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">City</label>
                               {isEditing ? (
                                 <input readOnly type="text" value={formData.serviceLocation?.city || ''} className="w-full px-3 py-2 border rounded-lg bg-gray-50" />
                               ) : (
                                 <p className="font-medium text-gray-900">{profileData?.serviceLocation?.city}</p>
                               )}
                             </div>
                             <div className="space-y-1">
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Zip Code</label>
                               {isEditing ? (
                                 <input type="text" value={formData.serviceLocation?.zipCode || ''} onChange={(e) => handleNestedChange('serviceLocation', 'zipCode', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                               ) : (
                                 <p className="font-medium text-gray-900">{profileData?.serviceLocation?.zipCode || 'N/A'}</p>
                               )}
                             </div>
                        </div>
                         {/* Preferred Hospital Section */}
                         <div className="mt-6">
                           <h4 className="text-md font-semibold text-gray-700 mb-2 flex items-center gap-2">
                             <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0-1.104.896-2 2-2s2 .896 2 2-.896 2-2 2-2-.896-2-2zm0 0V7m0 4v4m0 0c0 1.104-.896 2-2 2s-2-.896-2-2 .896-2 2-2 2 .896 2 2z" /></svg>
                             Preferred Hospital
                           </h4>
                           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                             <div className={`space-y-1 ${isEditing ? 'md:col-span-3' : 'md:col-span-2'}`}>
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Hospital</label>
                               {isEditing ? (
                                 <HospitalSearchBox
                                   value={formData.preferredHospital || {}}
                                   onSelect={hospital => setFormData(prev => ({
                                     ...prev,
                                     preferredHospital: hospital
                                   }))}
                                 />
                               ) : (
                                 <div>
                                   <p className="font-medium text-gray-900">{profileData?.preferredHospital?.name || 'Not set'}</p>
                                   <p className="text-gray-500 text-sm">{profileData?.preferredHospital?.address || ''}</p>
                                 </div>
                               )}
                             </div>
                             {!isEditing && (
                             <div className="space-y-1">
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                               <p className="font-medium text-gray-900">{profileData?.preferredHospital?.phone || 'Not set'}</p>
                             </div>
                             )}
                           </div>
                         </div>
                     </div>
                   </div>

                   {/* Medical Information - Customer Only */}
                   <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                     <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                       <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                       Medical Information
                     </h3>
                     <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                             <div>
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Blood Group</label>
                               {isEditing ? (
                                 <select 
                                   value={formData.medicalProfile?.bloodGroup || ''} 
                                   onChange={(e) => handleNestedChange('medicalProfile', 'bloodGroup', e.target.value)}
                                   className="w-full px-3 py-2 border rounded-lg bg-white"
                                 >
                                    <option value="">Select</option>
                                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                                 </select>
                               ) : (
                                 <p className="font-medium text-gray-900">{profileData?.medicalProfile?.bloodGroup || 'N/A'}</p>
                               )}
                             </div>
                             <div>
                               <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mobility Level</label>
                               {isEditing ? (
                                 <select 
                                   value={formData.medicalProfile?.mobilityLevel || ''} 
                                   onChange={(e) => handleNestedChange('medicalProfile', 'mobilityLevel', e.target.value)}
                                   className="w-full px-3 py-2 border rounded-lg bg-white"
                                 >
                                    <option value="">Select</option>
                                    <option value="independent">Independent</option>
                                    <option value="needs-assistance">Needs Assistance</option>
                                    <option value="wheelchair">Wheelchair</option>
                                    <option value="bedridden">Bedridden</option>
                                 </select>
                               ) : (
                                 <p className="font-medium text-gray-900 capitalize">{profileData?.medicalProfile?.mobilityLevel?.replace('-', ' ') || 'N/A'}</p>
                               )}
                             </div>
                        </div>

                        <div>
                           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Conditions (Chronic & Other)</label>
                           {isEditing ? (
                               <div className="space-y-3 mt-1">
                                   <div className="flex flex-wrap gap-2">
                                      {['diabetes', 'hypertension', 'heart-disease', 'dementia', 'arthritis', 'asthma', 'parkinsons', 'stroke-history', 'kidney-disease', 'cancer', 'copd', 'osteoporosis', 'other'].map(condition => (
                                        <button
                                            key={condition}
                                            type="button"
                                            onClick={() => handleConditionToggle(condition)}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                                                formData.medicalProfile?.chronicConditions?.includes(condition)
                                                ? 'bg-red-100 text-red-700 border-red-200 shadow-sm'
                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            {condition.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </button>
                                      ))}
                                   </div>
                                   {formData.medicalProfile?.chronicConditions?.includes('other') && (
                                       <div className="animate-fade-in">
                                            <input 
                                                type="text" 
                                                value={formData.medicalProfile?.otherConditions || ''}
                                                onChange={(e) => handleNestedChange('medicalProfile', 'otherConditions', e.target.value)}
                                                placeholder="Specify other condition..."
                                                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
                                            />
                                       </div>
                                   )}
                               </div>
                           ) : (
                               <div className="flex flex-wrap gap-2 mt-1">
                                  {profileData?.medicalProfile?.chronicConditions?.map(c => (
                                      <span key={c} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded capitalize">{c.replace('-', ' ')}</span>
                                  ))}
                                  {profileData?.medicalProfile?.otherConditions && (
                                      <span className="px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded">{profileData.medicalProfile.otherConditions}</span>
                                  )}
                                  {(!profileData?.medicalProfile?.chronicConditions?.length && !profileData?.medicalProfile?.otherConditions) && <span className="text-gray-400 text-sm">None listed</span>}
                               </div>
                           )}
                        </div>

                        <div>
                           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Allergies</label>
                           {isEditing ? (
                             <input type="text" value={formData.medicalProfile?.allergies || ''} onChange={(e) => handleNestedChange('medicalProfile', 'allergies', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                           ) : (
                             <p className="font-medium text-gray-900">{profileData?.medicalProfile?.allergies || 'None'}</p>
                           )}
                        </div>

                        <div>
                           <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dietary Restrictions</label>
                           {isEditing ? (
                             <input type="text" value={formData.medicalProfile?.dietaryRestrictions || ''} onChange={(e) => handleNestedChange('medicalProfile', 'dietaryRestrictions', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                           ) : (
                             <p className="font-medium text-gray-900">{profileData?.medicalProfile?.dietaryRestrictions || 'None'}</p>
                           )}
                        </div>
                     </div>
                   </div>

                   <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                     <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                       <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                       Emergency Contact
                     </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                             <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact Name</label>
                             {isEditing ? (
                               <input type="text" value={formData.emergencyContact?.name || ''} onChange={(e) => handleNestedChange('emergencyContact', 'name', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                             ) : (
                               <p className="font-medium text-gray-900">{profileData?.emergencyContact?.name || 'Not set'}</p>
                             )}
                        </div>
                        <div className="space-y-1">
                             <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                             {isEditing ? (
                               <input type="text" value={formData.emergencyContact?.phone || ''} onChange={(e) => handleNestedChange('emergencyContact', 'phone', e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
                             ) : (
                               <p className="font-medium text-gray-900">{profileData?.emergencyContact?.phone || 'Not set'}</p>
                             )}
                        </div>
                     </div>
                   </div>
                 </>
               )}

              {/* Payment Settings - Caregiver Only */}
              {user?.userType === 'caregiver' && (
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                    Payment Settings
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Digital Payments</h4>
                      <p className="text-sm text-gray-500">Allow customers to pay via digital wallet (secured)</p>
                    </div>
                     {isEditing ? (
                       <label className="relative inline-flex items-center cursor-pointer">
                         <input 
                           type="checkbox" 
                           checked={formData.acceptsDigitalPayment ?? true} 
                           onChange={(e) => setFormData(prev => ({ ...prev, acceptsDigitalPayment: e.target.checked }))} 
                           className="sr-only peer" 
                         />
                         <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                       </label>
                     ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${profileData?.acceptsDigitalPayment !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                           {profileData?.acceptsDigitalPayment !== false ? 'Active' : 'Disabled'}
                        </span>
                     )}
                  </div>
                </div>
              )}

              {/* Notification Settings */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                  Notification Settings
                </h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Push Notifications</h4>
                      <p className="text-sm text-gray-500">Receive specific updates on your device</p>
                    </div>
                    <button 
                         onClick={subscribeToPush} 
                         disabled={pushEnabled}
                         className={`px-4 py-2 rounded-lg text-sm font-medium ${pushEnabled ? 'bg-green-100 text-green-700 cursor-default' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                    >
                         {pushEnabled ? 'Enabled' : 'Enable'}
                    </button>
                  </div>
                  {/* <div className="border-t border-gray-200 pt-4">
                     <button
                        onClick={handleTestNotification}
                        className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:border-gray-400 hover:bg-gray-50 flex items-center justify-center gap-2"
                     >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        Send Test Notification
                     </button>
                     <p className="text-xs text-center text-gray-400 mt-2">
                       Click to verify that your browser receives notifications.
                     </p>
                  </div> */}
                </div>
              </div>

            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-8 flex justify-end gap-3 border-t pt-6">
            {!isEditing ? (
              <>
                 <button
                  onClick={logout}
                  className="px-4 py-2 text-red-600 font-medium hover:bg-red-50 rounded-lg transition-colors"
                >
                  Logout
                </button>
                <button
                  onClick={() => {
                    setIsEditing(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
                >
                  Edit Profile
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setIsEditing(false); setFormData(profileData!); }}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors shadow-lg disabled:opacity-50 flex items-center gap-2"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
            What Others Say
          </h3>

          {reviewsLoading ? (
             <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
               <p className="text-gray-500">No reviews yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
               {reviews.map((review) => (
                 <div key={review._id} className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                       <div className="flex items-center gap-3">
                          {user?.userType === 'caregiver' ? (
                            // Show Customer if user is caregiver
                             <img src={getFileUrl(review.customer?.profilePhoto || '')} alt="" className="w-10 h-10 rounded-full bg-gray-200 object-cover" />
                          ) : (
                            // Show Caregiver if user is customer
                             <img src={getFileUrl(review.caregiver?.profilePhoto || '')} alt="" className="w-10 h-10 rounded-full bg-gray-200 object-cover" />
                          )}
                          <div>
                            <p className="font-bold text-gray-900">
                               {user?.userType === 'caregiver' ? review.customer?.firstName : review.caregiver?.firstName}
                            </p>
                            <p className="text-xs text-gray-500">{new Date(review.createdAt).toLocaleDateString()}</p>
                          </div>
                       </div>
                       <div className="flex text-yellow-400">
                         {[...Array(5)].map((_, i) => (
                           <svg key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`} viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                         ))}
                       </div>
                    </div>
                    {review.reviewText && (
                      <p className="text-gray-600 text-sm leading-relaxed">"{review.reviewText}"</p>
                    )}
                 </div>
               ))}
            </div>
          )}
        </div>

      </div>

      
      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-900">Select Location</h3>
              <button 
                  type="button"
                  onClick={() => {
                    if (user?.userType === 'caregiver') {
                        setFormData(prev => ({...prev, serviceAreas: prevServiceAreas.current}));
                    }
                    setShowLocationPicker(false);
                  }} 
                  className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-4">
              {isMapsLoaded ? (
                user?.userType === 'caregiver' ? (
                     <div className="flex flex-col h-full max-h-[700px]">
                         <div className="flex-grow overflow-y-auto mb-2">
                             <ServiceAreaPicker
                                selectedAreas={initialServiceAreas}
                                onAreasChange={handleServiceAreasChange}
                             />
                         </div>
                         <div className="border-t pt-4 flex justify-end gap-3">
                             <button 
                                type="button"
                                onClick={() => {
                                    setFormData(prev => ({...prev, serviceAreas: prevServiceAreas.current}));
                                    setShowLocationPicker(false);
                                }} 
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                             >
                                Cancel
                             </button>
                             <button 
                                type="button"
                                onClick={() => setShowLocationPicker(false)} 
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 shadow-sm transition-colors"
                             >
                                Save Changes
                             </button>
                         </div>
                     </div>
                ) : (
                <LocationPicker 
                  onLocationSelect={handleLocationSelect}
                  isPrecise={user?.userType === 'customer'}
                  initialLocation={
                    user?.userType === 'customer' && formData.serviceLocation?.coordinates
                      ? { lat: formData.serviceLocation.coordinates[1], lng: formData.serviceLocation.coordinates[0], address: formData.serviceLocation.address }
                      : undefined
                  }
                />
                )
              ) : (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
